export type LessonLevel = "absolute_beginner" | "beginner" | "intermediate" | "advanced";

export type SessionConnectionState = "disconnected" | "connecting" | "connected" | "error";

export type SessionAgentState =
  | "thinking"
  | "speaking"
  | "idle"
  | "listening"
  | "connecting"
  | "disconnected"
  | "error";

export interface SessionHeaderContract {
  isActiveSession: boolean;
  sessionDuration: number;
  selectedLanguageCode?: string;
  selectedLanguageLevel?: LessonLevel;
  showLanguageLevel?: boolean;
  isMuted?: boolean;
  micActivityLevel?: number;
  onToggleMute?: () => void;
  onClose?: () => void;
  assistantDisplayName?: string;
  disableAvatar?: boolean;
  isZenMode?: boolean;
  connectionState?: SessionConnectionState;
}

export interface TranscriptionMessageContract {
  id?: string;
  role: "assistant" | "user";
  content: string;
  isFinal?: boolean;
  isLastMessage?: boolean;
  agentState?: SessionAgentState;
  showWordTapHint?: boolean;
  assistantLabel?: string;
  isZenMode?: boolean;
  translation?: string;
  translationLanguage?: string;
  translationStatus?: "loading" | "success" | "error";
  onRetryTranslation?: () => void;
}

export const sessionCopy = {
  brandAssistantName: "Chicky Tutor",
  assistantRoleLabel: "AI Language Tutor",
  userRoleLabel: "You",
  connected: "Connected",
  disconnected: "Disconnected",
  connecting: "Connecting...",
  error: "Connection Error",
  speaking: "Chicky is speaking...",
  listening: "You're speaking...",
  liveBadge: "Live",
  wordTapHint: "Tap or select text to ask Chicky",
  emptyStatePrompt: 'Say "Hello" to start!',
  loadingPrompt: "Loading...",
  endSessionTitle: "End Session",
  endSessionMessage: "Are you sure you want to end this lesson session?",
  endSessionConfirm: "End Session",
  endSessionCancel: "Continue Learning",
} as const;

export function formatSessionDuration(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (safeSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export function getLanguageDisplayName(
  code: string | undefined,
  languageMap: Record<string, string>,
  fallback = "English"
): string {
  if (!code) {
    return fallback;
  }

  for (const [name, languageCode] of Object.entries(languageMap)) {
    if (languageCode === code) {
      return name;
    }
  }

  return code;
}

export function resolveAgentState(params: {
  connectionState: SessionConnectionState;
  isAgentSpeaking: boolean;
  isUserSpeaking: boolean;
}): SessionAgentState {
  const { connectionState, isAgentSpeaking, isUserSpeaking } = params;

  if (connectionState === "connecting") return "connecting";
  if (connectionState === "error") return "error";
  if (connectionState === "disconnected") return "disconnected";
  if (isAgentSpeaking) return "speaking";
  if (isUserSpeaking) return "listening";
  return "idle";
}
